/*
  OCR tool. Upload an image, get the extracted text.
*/
import { useRef, useState } from "react";
import { Upload, Copy, Check, Image as ImageIcon, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { phase3Api } from "../lib/phase3Api";

export default function OcrPage() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function pick(file) {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setText("");
    setBusy(true);
    try {
      const res = await phase3Api.ocr(file);
      setText(res.text || "");
      if (!res.text) toast.info("Couldn't find any readable text");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed");
    } finally { setBusy(false); }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy blocked"); }
  }

  return (
    <div className="animate-fade-up max-w-2xl">
      <span className="label-caps">Quick tool</span>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Image → Text.</h1>
      <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
        Upload a photo of anything — handwriting, receipts, whiteboards, menus. We extract the text.
      </p>

      <div className="card-tactile rounded-2xl p-5 mt-6">
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          data-testid="ocr-file"
          onChange={(e) => pick(e.target.files?.[0])} />
        <button onClick={() => fileRef.current?.click()}
          data-testid="ocr-upload"
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--primary)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {preview ? "Try another image" : "Upload image"}
        </button>
        {preview && (
          <div className="mt-4 rounded-xl overflow-hidden border max-h-64 flex items-center justify-center"
            style={{ borderColor: "var(--border-subtle)", background: "var(--surface-2)" }}>
            <img src={preview} alt="Preview" className="max-h-64 object-contain" />
          </div>
        )}
      </div>

      {(busy || text) && (
        <div className="card-tactile rounded-2xl p-5 mt-5" data-testid="ocr-result">
          <div className="flex items-center justify-between mb-2">
            <div className="label-caps flex items-center gap-1"><Sparkles className="h-3 w-3" /> Extracted text</div>
            {text && (
              <button onClick={copy} className="inline-flex items-center gap-1 text-xs font-semibold">
                {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
              </button>
            )}
          </div>
          {busy ? (
            <div className="py-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Reading image…</div>
          ) : (
            <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed" style={{ color: "var(--text-main)" }}>{text}</pre>
          )}
        </div>
      )}

      <div className="mt-6 text-xs flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
        <ImageIcon className="h-3 w-3" />
        <span>PNG, JPEG, WebP, HEIC. Max 8 MB. Never stored — image is deleted from our server after extraction.</span>
      </div>
    </div>
  );
}
